import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Download } from 'lucide-react';
import { useState } from 'react';
import { usePermission } from '../../auth/permissions';
import type { Charger } from '../../api/chargers';
import { DataTable } from '../../components/DataTable';
import { EmptyState } from '../../components/EmptyState';
import { FieldRow, FormSection } from '../../components/form/FieldRow';
import { useCommand } from './useCommand';

interface Row {
  key: string;
  value: string | null;
}

/** The charger's own settings (OCPP configuration keys), read remotely (managers too) and changed (admins). */
export function ConfigurationTab({ charger: c }: { charger: Charger }) {
  const { run, busy, action } = useCommand(c);
  const canChange = usePermission('chargers.configure');
  const [key, setKey] = useState('');
  const [value, setValue] = useState('');
  const rows: Row[] = Object.entries(c.lastConfiguration ?? {}).map(([k, v]) => ({ key: k, value: v }));

  return (
    <Box sx={{ display: 'grid', gap: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Typography color="textSecondary">{rows.length ? `${rows.length} settings, as the charger last reported them.` : 'Not read yet.'}</Typography>
        <Button startIcon={<Download size={18} strokeWidth={1.75} />} disabled={busy || !c.isConnected} onClick={() => void run({ action: 'GetConfiguration' })}>
          {action === 'GetConfiguration' ? 'Reading…' : 'Read from the charger'}
        </Button>
      </Box>

      {canChange && (
      <FormSection title="Change a setting">
        <FieldRow columns="2fr 1fr auto">
          <Autocomplete freeSolo options={rows.map((r) => r.key)} inputValue={key} onInputChange={(_e, v) => setKey(v)} renderInput={(p) => <TextField {...p} label="Key" placeholder="e.g. MeterValueSampleInterval" />} />
          <TextField label="New value" value={value} onChange={(e) => setValue(e.target.value)} />
          <Button
            variant="contained"
            sx={{ alignSelf: 'start', minHeight: 50 }}
            disabled={busy || !key.trim() || !c.isConnected}
            onClick={async () => {
              const r = await run({ action: 'ChangeConfiguration', key: key.trim(), value });
              if (r?.status === 'accepted') void run({ action: 'GetConfiguration', key: [key.trim()] });
            }}
          >
            Send
          </Button>
        </FieldRow>
        <Typography variant="body2" color="textSecondary">Some settings only take effect after a restart. The password is changed under Security.</Typography>
      </FormSection>
      )}

      <DataTable
        label="Configuration"
        rows={rows}
        columns={[
          { key: 'key', header: 'Key', sortValue: (r) => r.key.toLowerCase(), render: (r) => <span className="num">{r.key}</span> },
          { key: 'value', header: 'Value', render: (r) => <Box component="span" className="num" sx={{ wordBreak: 'break-all' }}>{r.value ?? '—'}</Box> },
        ]}
        rowKey={(r) => r.key}
        searchText={(r) => `${r.key} ${r.value ?? ''}`}
        searchPlaceholder="Search the settings"
        initialSort={{ key: 'key', direction: 'asc' }}
        pageSize={100}
        onRowClick={
          canChange
            ? (r) => {
                setKey(r.key);
                setValue(r.value ?? '');
              }
            : undefined
        }
        empty={<EmptyState title="No configuration yet">Use Read from the charger to see its settings.</EmptyState>}
      />
    </Box>
  );
}
